import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useFensterverhalten } from './Nachfrage'
import { satz, t, zeitpunkt } from './sprache'
import {
  abonniereStoerungen,
  knopflosSeit,
  quittiereStoerungen,
  stoerungen,
  wartetSeit,
} from './Stoerungsstelle'
import type { Stoerungseintrag } from './Stoerungsstelle'

/**
 * Der Weg aus einer Störung — der des Dienstes, wo er einen kennt, sonst:
 * erneut versuchen, und besteht es fort, dem Betrieb melden, mit Uhrzeit und
 * Kennung (7.2 Abschnitt 30, R1005).
 */
export function stoerungsweg(eintrag: Stoerungseintrag): string {
  const s = eintrag.stoerung
  if (s.handreichung) {
    return satz(s.handreichung)
  }

  // **Die Kennung steht nicht im Satz** (7.2 Abschnitt 37, R1145): Sie reist
  // über den Knopf „Für den Betrieb kopieren", zusammen mit der Uhrzeit.
  return s.kennung
    ? t('stoerung.weg.kennung')
    : t('stoerung.weg.allgemein', { zeit: zeitpunkt(s.zeit.toISOString()) })
}

/**
 * Uhrzeit und Kennung für den Betrieb — in die Zwischenablage, nicht in den
 * Satz an den Anwender (7.2 Abschnitt 37, R1145). Bis zum 2026-10-06 stand
 * eine 55-stellige Kennung mitten im Satz.
 *
 * **Gibt der Browser keine Zwischenablage her, steht der Knopf nicht da** —
 * dieselbe Haltung wie bei der Objekt-ID (R155): Dann steht die Kennung
 * gedämpft daneben, damit sie sich markieren lässt.
 */
function Betriebskopie({ zeit, kennung }: { zeit: Date; kennung: string }) {
  const [kopiert, setKopiert] = useState(false)
  const text = t('stoerung.kopie', { zeit: zeitpunkt(zeit.toISOString()), kennung })
  const gehtKopieren = typeof navigator !== 'undefined' && !!navigator.clipboard

  if (!gehtKopieren) {
    return <p className="erlaeuterung betriebskopie">{text}</p>
  }

  return (
    <p className="betriebskopie">
      <button
        type="button"
        className="schmal"
        onClick={() =>
          void navigator.clipboard
            .writeText(text)
            .then(() => setKopiert(true))
            .catch(() => setKopiert(false))
        }
      >
        {t('stoerung.kopieren')}
      </button>{' '}
      <span role="status" className="erlaeuterung">
        {kopiert ? t('stoerung.kopiert') : ''}
      </span>
    </p>
  )
}

/** Was nicht geschehen ist — der Satz des Dienstes, sonst der der Anwendung. */
export function stoerungssatz(eintrag: Stoerungseintrag): string {
  const s = eintrag.stoerung
  const text = s.meldung ? satz(s.meldung) : t(s.schluessel)
  return eintrag.anzahl > 1 ? `${text} (${t('stoerung.anzahl', { anzahl: String(eintrag.anzahl) })})` : text
}

/**
 * **Jede technische Störung endet in diesem Fenster** (7.2 Abschnitt 30,
 * R1003, R1006) — auf jedem Bildschirm, einmal eingehängt um die ganze
 * Anwendung. Fachliche Befunde öffnen es nie; sie stehen am Ort (R1004).
 *
 * Mehrere Störungen stehen untereinander, gleiche einmal mit ihrer Zahl. Eine
 * Taste, weil es nichts abzubrechen gibt.
 */
export function Stoerungsfenster() {
  const eintraege = useSyncExternalStore(abonniereStoerungen, stoerungen)
  if (eintraege.length === 0) {
    return null
  }

  return <Stoerungsliste eintraege={eintraege} />
}

function Stoerungsliste({ eintraege }: { eintraege: Stoerungseintrag[] }) {
  const verstanden = useRef<HTMLButtonElement>(null)
  useFensterverhalten(quittiereStoerungen, verstanden)

  return (
    <div className="nachweisschatten stoerungsschatten" onClick={quittiereStoerungen}>
      <div
        className="fenster stoerungsfenster"
        role="alertdialog"
        aria-label={t('stoerung.titel')}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2>{t('stoerung.titel')}</h2>
        </header>

        {eintraege.map((e, i) => (
          <div key={i} className="stoerung">
            <p className="probe abweichung">{stoerungssatz(e)}</p>
            <p className="erlaeuterung">{stoerungsweg(e)}</p>
            {e.stoerung.kennung && <Betriebskopie zeit={e.stoerung.zeit} kennung={e.stoerung.kennung} />}
          </div>
        ))}

        <div className="aktionsreihe">
          <button type="button" ref={verstanden} onClick={quittiereStoerungen}>
            {t('stoerung.verstanden')}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Ab wann ein Abruf als Warten gilt, das der Anwender sehen soll. */
const AB = 2000

/**
 * Ab wann, wenn der Knopf mit dem Klick verschwunden ist (7.2 Abschnitt 40,
 * R1251) — sofort, nur ohne Flackern bei einer schnellen Antwort.
 */
const KNOPFLOS_AB = 300

/**
 * **Wer wartet, sieht es** (7.2 Abschnitt 30, R1005) — die Auflage des
 * Auftraggebers: *„Dabei bitte mit dem Anwender immer kommunizieren."* Dauert
 * ein Abruf länger als zwei Sekunden, steht am unteren Rand, seit wann die
 * Anwendung auf den Dienst wartet.
 */
export function Wartehinweis() {
  const [jetzt, setJetzt] = useState(() => Date.now())

  useEffect(() => {
    const uhr = window.setInterval(() => setJetzt(Date.now()), 250)
    return () => window.clearInterval(uhr)
  }, [])

  const seit = wartetSeit()
  const knopflos = knopflosSeit()
  if (seit === null || (jetzt - seit < AB && (knopflos === null || jetzt - knopflos < KNOPFLOS_AB))) {
    return null
  }

  return (
    <div className="wartehinweis" role="status">
      {t('warten.hinweis', { sekunden: String(Math.floor((jetzt - seit) / 1000)) })}
    </div>
  )
}
